import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
    Backdrop, Box, CircularProgress, IconButton, Typography, Chip,
    Accordion, AccordionSummary, AccordionDetails, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { initializeDB } from '../../../config/db';
import { JOB_STORE, TOOL_INVENTORY_STORE, TOOL_CALIBRATION_STORE, TOOL_DOCS_STORE } from '../../../config/constants';
import SignatureModalWithAuto from '../../common/SignatureModalWithAuto';
import SelectSingleEmployeeModal from '../../shared/SelectSingleEmployeeModal';
import { generateUUID } from '../../../utils/uuid';
import Swal from 'sweetalert2';
import gasMonitorTestService from '../../../services/gasMonitorTestService';
import { OPERATION_TYPES, TOOL_TYPE_FILTERS, CALIBRATION_TOOL_FILTER, STATUS_DRAFT, STATUS_SUBMITTED } from './constants';
import BumpTestForm from './BumpTestForm';
import CalibrationView from './CalibrationView';

const GasMonitorTestForm = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const uuid = searchParams.get('uuid');
    const userFullName = localStorage.getItem('employeeFullName') || '';
    const userId = localStorage.getItem('employeeId') || '';

    const [jobs, setJobs] = useState([]);
    const [tools, setTools] = useState([]);
    const [allCalibrations, setAllCalibrations] = useState([]);
    const [form, setForm] = useState({
        uuid: uuid || generateUUID(),
        operationType: 'bump_test',
        toolType: 'Gas Monitor',
        status: STATUS_DRAFT,
        toolPart: '', toolSerialNo: '', toolModel: '', toolManufacturer: '', toolDescription: '',
        jobCode: '', customerName: '', jobLocation: '',
        testDate: dayjs().format('YYYY-MM-DD'),
        h2sResult: '', coResult: '', o2Result: '', lelResult: '', overallResult: '',
        employeeId: userId, employeeName: userFullName,
        signature: '', signatureDate: dayjs().format('YYYY-MM-DD'),
        comments: '',
        createdAt: new Date().toISOString(), updatedAt: new Date().toISOString()
    });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [employeeModalOpen, setEmployeeModalOpen] = useState(false);

    const saveTimer = useRef(null);
    const sigPadRef = useRef(null);
    const isBumpTest = form.operationType === 'bump_test';

    const sortedJobs = useMemo(() =>
        jobs.map(j => ({ ...j, label: `${j.Job} - ${j.JobName}` }))
            .sort((a, b) => (parseInt(b.Job) || 0) - (parseInt(a.Job) || 0)),
    [jobs]);

    // Bump Test: все Gas Monitor + Sniffer
    const filteredTools = useMemo(() =>
        tools
            .filter(t => TOOL_TYPE_FILTERS.some(f => (t.type || '').toLowerCase().includes(f.toLowerCase())))
            .map(t => ({ ...t, label: `${t.part || ''} — ${t.serialNo || ''} — ${t.model || ''} (${t.manufacturer || ''})` })),
    [tools]);

    // Calibration: Type=Gas Monitor AND Description=Gas Monitor
    const calibrationTools = useMemo(() =>
        tools
            .filter(t =>
                (t.type || '').toLowerCase() === CALIBRATION_TOOL_FILTER.type.toLowerCase() &&
                (t.description || '').toLowerCase() === CALIBRATION_TOOL_FILTER.description.toLowerCase()
            )
            .map(t => ({ ...t, label: `${t.part || ''} — ${t.serialNo || ''} — ${t.model || ''} (${t.manufacturer || ''})` })),
    [tools]);

    // Auto-compute overall result
    useEffect(() => {
        if (!isBumpTest) return;
        const readings = [form.h2sResult, form.coResult, form.o2Result, form.lelResult];
        const allSet = readings.every(r => r === 'pass' || r === 'fail');
        if (!allSet) {
            if (form.overallResult !== '') { const u = { ...form, overallResult: '' }; setForm(u); queueSave(u); }
            return;
        }
        const newOverall = readings.every(r => r === 'pass') ? 'pass' : 'fail';
        if (form.overallResult !== newOverall) { const u = { ...form, overallResult: newOverall }; setForm(u); queueSave(u); }
    }, [form.h2sResult, form.coResult, form.o2Result, form.lelResult]);

    // Load data
    useEffect(() => {
        (async () => {
            const db = await initializeDB();
            if (db.objectStoreNames.contains(JOB_STORE)) setJobs(await db.transaction(JOB_STORE, 'readonly').objectStore(JOB_STORE).getAll() || []);
            if (db.objectStoreNames.contains(TOOL_INVENTORY_STORE)) setTools(await db.transaction(TOOL_INVENTORY_STORE, 'readonly').objectStore(TOOL_INVENTORY_STORE).getAll() || []);
            if (db.objectStoreNames.contains(TOOL_CALIBRATION_STORE)) setAllCalibrations(await db.transaction(TOOL_CALIBRATION_STORE, 'readonly').objectStore(TOOL_CALIBRATION_STORE).getAll() || []);
            if (uuid) {
                const existing = await gasMonitorTestService.loadByUuid(uuid);
                if (existing) { setForm(existing); setSubmitted(existing.status === STATUS_SUBMITTED); }
            }
        })();
    }, [uuid]);

    const queueSave = (d) => {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => { gasMonitorTestService.saveDraft(d).catch(console.error); }, 1000);
    };

    const handleFieldChange = (field, value) => { const u = { ...form, [field]: value }; setForm(u); queueSave(u); };

    const handleJobChange = (_, v) => {
        const u = v ? { ...form, jobCode: v.Job || '', customerName: v.Customer || '', jobLocation: v.Location || v.location || '' }
                    : { ...form, jobCode: '', customerName: '', jobLocation: '' };
        setForm(u); queueSave(u);
    };

    const handleToolChange = (_, v) => {
        const u = v ? { ...form, toolPart: v.part || '', toolSerialNo: v.serialNo || '', toolModel: v.model || '', toolManufacturer: v.manufacturer || '', toolDescription: v.description || '' }
                    : { ...form, toolPart: '', toolSerialNo: '', toolModel: '', toolManufacturer: '', toolDescription: '' };
        setForm(u); queueSave(u);
    };

    const handleGasResult = (key, value) => { if (value) handleFieldChange(key, value); };

    const handleSignatureAccept = () => {
        if (!sigPadRef.current) return;
        const u = { ...form, signature: sigPadRef.current.toDataURL(), signatureDate: dayjs().format('YYYY-MM-DD') };
        setForm(u); queueSave(u); setSignatureModalOpen(false);
    };

    const handleSelectEmployee = (p) => {
        const u = { ...form, employeeId: p.id || '', employeeName: p.FullName || p.name || '' };
        setForm(u); queueSave(u); setEmployeeModalOpen(false);
    };

    const handleSubmit = async () => {
        const newErrors = {};
        if (!form.jobCode) newErrors.jobCode = 'Job is required';
        if (!form.toolPart) newErrors.toolPart = 'Tool is required';
        if (!form.h2sResult) newErrors.h2sResult = 'Required';
        if (!form.coResult) newErrors.coResult = 'Required';
        if (!form.o2Result) newErrors.o2Result = 'Required';
        if (!form.lelResult) newErrors.lelResult = 'Required';
        if (!form.signature) newErrors.signature = 'Signature is required';
        if (!form.employeeName) newErrors.employeeName = 'Employee name is required';
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
            Swal.fire({ icon: 'error', title: 'Validation Error', text: 'Please fill in all required fields' });
            return;
        }
        const result = await Swal.fire({ title: 'Submit Form?', text: 'Once submitted, the form cannot be edited', icon: 'question', showCancelButton: true, confirmButtonText: 'Yes, submit it' });
        if (!result.isConfirmed) return;
        try {
            setSaving(true);
            const submitData = { ...form, status: STATUS_SUBMITTED };
            // Отправляем на сервер
            const response = await gasMonitorTestService.submit(submitData);
            // Удаляем драфт из IndexedDB
            await gasMonitorTestService.delete(form.uuid);
            // Вставляем новый ToolDoc в IndexedDB сразу (без ожидания sync)
            if (response?.toolDoc) {
                try {
                    const db = await initializeDB();
                    if (db.objectStoreNames.contains(TOOL_DOCS_STORE)) {
                        const tx = db.transaction(TOOL_DOCS_STORE, 'readwrite');
                        await tx.objectStore(TOOL_DOCS_STORE).put(response.toolDoc);
                        await tx.done;
                    }
                } catch (e) { console.error('Failed to save toolDoc to IndexedDB:', e); }
            }
            Swal.fire({ icon: 'success', title: 'Success!', text: 'Gas Monitor Test submitted' });
            navigate('/operations/gas-monitor-test');
        } catch (e) {
            console.error('Submit error:', e);
            Swal.fire({ icon: 'error', title: 'Error', text: e.response?.data?.message || 'Failed to submit form' });
        } finally { setSaving(false); }
    };

    const isReadOnly = submitted;

    return (
        <Box sx={{ height: 'calc(100vh - 60px)', backgroundColor: '#f5f5f5', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate('/operations/gas-monitor-test')} edge="start"><ArrowBackIcon /></IconButton>
                        <Typography variant="h6" fontWeight={600}>
                            {isBumpTest ? 'GAS MONITOR — BUMP TEST' : 'GAS MONITOR — CALIBRATION'}
                        </Typography>
                    </Box>
                    {isBumpTest && <Chip label={submitted ? 'Submitted' : 'Draft'} size="small" sx={{ backgroundColor: submitted ? '#4caf50' : '#9e9e9e', color: 'white' }} />}
                </Box>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto' }}>
                <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                    {/* Operation Type Toggle */}
                    <Accordion defaultExpanded sx={{ mb: 0.5, '&.Mui-expanded': { margin: '0 0 4px 0' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}
                            sx={{ minHeight: '40px !important', '& .MuiAccordionSummary-content': { margin: '6px 0 !important' } }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Operation Type</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            <ToggleButtonGroup value={form.operationType} exclusive fullWidth size="small" disabled={isReadOnly}
                                onChange={(_, v) => { if (v) handleFieldChange('operationType', v); }}>
                                {OPERATION_TYPES.map(op => (
                                    <ToggleButton key={op.value} value={op.value} sx={{
                                        '&.Mui-selected': { backgroundColor: '#1976d2', color: '#fff', '&:hover': { backgroundColor: '#1565c0' } }
                                    }}>{op.label}</ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        </AccordionDetails>
                    </Accordion>

                    {isBumpTest ? (
                        <BumpTestForm
                            form={form} errors={errors} isReadOnly={isReadOnly} saving={saving}
                            sortedJobs={sortedJobs} filteredTools={filteredTools}
                            onFieldChange={handleFieldChange} onJobChange={handleJobChange}
                            onToolChange={handleToolChange} onGasResult={handleGasResult}
                            onOpenSignature={() => setSignatureModalOpen(true)}
                            onClearSignature={() => handleFieldChange('signature', '')}
                            onOpenEmployeeModal={() => setEmployeeModalOpen(true)}
                            onSubmit={handleSubmit}
                        />
                    ) : (
                        <CalibrationView filteredTools={calibrationTools} allCalibrations={allCalibrations} />
                    )}
                </Box>
            </Box>

            <SignatureModalWithAuto open={signatureModalOpen} onClose={() => setSignatureModalOpen(false)}
                sigPadRef={sigPadRef} employeeName={form.employeeName} onAccept={handleSignatureAccept} existingSignature={form.signature} />
            <SelectSingleEmployeeModal open={employeeModalOpen} onClose={() => setEmployeeModalOpen(false)} onSelect={handleSelectEmployee} />
            <Backdrop open={saving} sx={{ zIndex: 9999 }}><CircularProgress color="inherit" /></Backdrop>
        </Box>
    );
};

export default GasMonitorTestForm;
